import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import type { Place, PlaceKind } from "@instead/shared";
import { api } from "../lib/api.ts";
import { drafts } from "../lib/drafts.ts";
import { useT } from "../lib/i18n.ts";
import { Ic } from "../components/Icons.tsx";
import { NeedChips } from "../components/NeedChips.tsx";
import { ChipGroup, Dialog, Info, Sheet, Toast, useToast } from "../components/Ui.tsx";

const RADII = [50, 150, 400, 1000];

/**
 * A place (rounds 2–4): name, indoor/outdoor, an optional map-picked spot
 * with a radius, what it offers, and the items bound to it. The map is for
 * choosing only — matching runs on the device and coordinates are saved,
 * never tracked (§5.10).
 */
export function PlaceEdit() {
  const t = useT();
  const qc = useQueryClient();
  const nav = useNavigate();
  const { id } = useParams({ from: "/settings/places/$id" });
  const isNew = id === "new";
  const { data: place } = useQuery({ queryKey: ["places", id], queryFn: () => api.places.get(id), enabled: !isNew });
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: api.settings.get });
  const needsOn = settings?.features.needs ?? false;
  const { data: items } = useQuery({ queryKey: ["items", "place", id], queryFn: () => api.items.list({ placeId: id, limit: "50" }), enabled: !isNew });
  // unsaved edits wait on the device while the needs editor is open (round 5)
  const draftKey = `place.${id}`;
  type PlaceDraft = { name: string; kind: PlaceKind; needIds: string[]; spot: { lat: number; lng: number; radiusM: number } | null };
  const draft = drafts.read<PlaceDraft>(draftKey);
  const [name, setName] = useState(draft?.name ?? "");
  const [kind, setKind] = useState<PlaceKind>(draft?.kind ?? "indoor");
  const [needIds, setNeedIds] = useState<string[]>(draft?.needIds ?? []);
  const [spot, setSpot] = useState<{ lat: number; lng: number; radiusM: number } | null>(draft?.spot ?? null);
  const [loaded, setLoaded] = useState(isNew || !!draft);
  const [mapOpen, setMapOpen] = useState(false);
  const [confirm, setConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, say] = useToast();

  if (place && !loaded) {
    setName(place.name);
    setKind(place.kind);
    setNeedIds(place.needIds);
    setSpot(place.lat != null && place.lng != null ? { lat: place.lat, lng: place.lng, radiusM: place.radiusM ?? 150 } : null);
    setLoaded(true);
  }

  const goEditNeeds = () => {
    drafts.write(draftKey, { name, kind, needIds, spot });
    void nav({ to: "/settings/needs", search: { back: `/settings/places/${id}` } as never });
  };
  const leave = () => { drafts.clear(draftKey); return nav({ to: "/settings/places" }); };

  async function save() {
    setError(null);
    const body: Omit<Place, "id" | "itemCount"> = { name: name.trim(), kind, needIds, lat: spot?.lat ?? null, lng: spot?.lng ?? null, radiusM: spot?.radiusM ?? null };
    try {
      if (isNew) await api.places.create(body);
      else await api.places.update(id, body);
      await qc.invalidateQueries({ queryKey: ["places"] });
      await leave();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't save.");
    }
  }

  async function remove() {
    await api.places.remove(id);
    await qc.invalidateQueries({ queryKey: ["places"] });
    await qc.invalidateQueries({ queryKey: ["items"] });
    await leave();
  }

  return (
    <form onSubmit={(e) => { e.preventDefault(); if (name.trim()) void save(); }}>
      <div className="screen-head">
        <h1 className="visually-hidden">{isNew ? t("places.new") : name}</h1>
        <button type="button" className="icon-btn" onClick={leave}>{t("cancel")}</button>
        <span style={{ flex: 1 }} />
        <button type="submit" className="btn btn-primary" style={{ minHeight: 36 }} disabled={!name.trim()}>{t("save")}</button>
      </div>
      <div className="title-line">
        <input aria-label={t("places.name")} autoFocus={isNew} maxLength={60} value={name} onChange={(e) => setName(e.target.value)} />
        <span style={{ color: "var(--ink-3)", display: "inline-flex" }}>{Ic.edit({ size: 14 })}</span>
      </div>

      <div className="section">
        <div className="eyebrow">{t("places.kind")}</div>
        <ChipGroup label={t("places.kind")} selected={[kind]} allowNone={false}
          options={[{ value: "indoor", label: t("places.indoor") }, { value: "outdoor", label: t("places.outdoor") }]}
          onChange={(v) => setKind((v[0] as PlaceKind) ?? "indoor")} />
      </div>

      <div className="section">
        <div className="eyebrow">{t("places.map")} <Info text={t("places.mapInfo")} label={t("places.map")} /></div>
        <button type="button" className="picker" style={{ width: "100%", minHeight: 52, padding: "var(--sp-2) var(--sp-3)" }} onClick={() => setMapOpen(true)}>
          <span style={{ color: "var(--accent)", display: "inline-flex" }}>{Ic.place()}</span>
          <span className="grow">{spot ? t("places.spot", { r: spot.radiusM }) : t("places.noSpot")}</span>
          {Ic.chevron({ size: 16 })}
        </button>
        {spot && <button type="button" className="chip" style={{ marginTop: "var(--sp-2)" }} onClick={() => setSpot(null)}>{t("remove")}</button>}
      </div>

      {needsOn && <div className="section">
        <div className="eyebrow">
          {t("places.offers")}
          <button type="button" className="icon-btn" style={{ minWidth: 28, minHeight: 28, padding: 0, marginLeft: "var(--sp-1)" }} aria-label={t("add.editNeeds")} title={t("add.editNeeds")} onClick={goEditNeeds}>{Ic.edit({ size: 13 })}</button>
        </div>
        <NeedChips label={t("places.offers")} selected={needIds} onChange={setNeedIds} />
      </div>}

      {!isNew && (items?.items.length ?? 0) > 0 && (
        <div className="section">
          <div className="eyebrow">{t("places.itemsHere")}</div>
          {items!.items.map((i) => (
            <div key={i.id} className="nav-row" style={{ cursor: "default" }}><span className="grow">{i.title}</span><span className="meta">{i.durationMin ? `${i.durationMin} min${i.durationMaxMin == null ? " +" : ""}` : ""}</span></div>
          ))}
        </div>
      )}

      {error && <p role="alert" className="meta" style={{ color: "var(--danger)", marginTop: "var(--sp-3)" }}>{error}</p>}

      {!isNew && (
        <div style={{ display: "flex", justifyContent: "center", marginTop: "var(--sp-5)" }}>
          <button type="button" className="icon-btn danger" onClick={() => setConfirm(true)}>{Ic.trash({ size: 14 })} {t("places.delete")}</button>
        </div>
      )}

      {mapOpen && <MapPicker initial={spot} onClose={() => setMapOpen(false)} onSave={(s) => { setSpot(s); setMapOpen(false); }} />}
      {confirm && (
        <Dialog title={t("places.deleteQ", { name })} onClose={() => setConfirm(false)}>
          <div className="dialog-actions">
            <button type="button" className="btn" onClick={() => setConfirm(false)}>{t("keep")}</button>
            <button type="button" className="btn btn-danger" onClick={remove}>{t("delete")}</button>
          </div>
        </Dialog>
      )}
      <Toast msg={toast} />
    </form>
  );
}

/** OpenStreetMap tiles (EU-hosted by the OSM Foundation); a pin + a radius circle. */
function MapPicker({ initial, onClose, onSave }: {
  initial: { lat: number; lng: number; radiusM: number } | null;
  onClose: () => void;
  onSave: (s: { lat: number; lng: number; radiusM: number }) => void;
}) {
  const t = useT();
  const el = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const circleRef = useRef<L.Circle | null>(null);
  const [pos, setPos] = useState<{ lat: number; lng: number } | null>(initial ? { lat: initial.lat, lng: initial.lng } : null);
  const [radius, setRadius] = useState(initial?.radiusM ?? 150);

  useEffect(() => {
    if (!el.current || mapRef.current) return;
    const start = pos ?? { lat: 45.46, lng: 9.19 };
    const map = L.map(el.current, { zoomControl: true, attributionControl: true }).setView([start.lat, start.lng], pos ? 15 : 12);
    L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener noreferrer">OpenStreetMap</a>',
    }).addTo(map);
    map.on("click", (e: L.LeafletMouseEvent) => setPos({ lat: e.latlng.lat, lng: e.latlng.lng }));
    mapRef.current = map;
    if (!pos && "geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition((p) => map.setView([p.coords.latitude, p.coords.longitude], 15), () => {}, { timeout: 6000 });
    }
    return () => { map.remove(); mapRef.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    circleRef.current?.remove();
    if (pos) {
      circleRef.current = L.circle([pos.lat, pos.lng], { radius: radius / 2, color: "#4e6f5c", weight: 2, dashArray: "6 6", fillOpacity: 0.08 }).addTo(map);
    }
  }, [pos, radius]);

  return (
    <Sheet title={t("places.mapTitle")} onClose={onClose}
      action={<button type="button" className="btn btn-primary" style={{ minHeight: 36 }} disabled={!pos} onClick={() => pos && onSave({ ...pos, radiusM: radius })}>{t("save")}</button>}>
      <div className="map" ref={el} role="application" aria-label={t("places.mapTitle")} />
      <p className="micro" style={{ marginTop: "var(--sp-2)" }}>{t("places.osmNote")}</p>
      <div className="map-radius" role="group" aria-label={t("places.spot", { r: radius })}>
        {RADII.map((r) => (
          <button key={r} type="button" className="chip" aria-pressed={radius === r} onClick={() => setRadius(r)}>{r >= 1000 ? `${r / 1000} km` : `${r} m`}</button>
        ))}
      </div>
    </Sheet>
  );
}
